import { urlRegex } from 'react-linkify-it'

import {
  workflowRoutes,
  WorkflowUrlParamKey,
} from '~/client/src/shared/constants/workflowRoutes'
import { stripMobileSuffix } from '~/client/src/shared/utils/authPortalHosts'

export interface IWorkflowLinkRef {
  projectCode: string
  workflowId: string
}

export const MAX_WORKFLOW_CARDS_PER_MESSAGE = 3

const workflowPathRegex = new RegExp(
  `^${workflowRoutes.WORKFLOWS.replace(
    ':projectCode',
    '([^/]+)',
  )}(?:/([^/]+))?/?$`,
)

function canonicalizeHost(host: string): string {
  const [hostname, port] = host.toLowerCase().split(':')
  const canonicalHostname = stripMobileSuffix(hostname)

  return port ? `${canonicalHostname}:${port}` : canonicalHostname
}

export function parseWorkflowLink(
  url: string,
  currentHost: string,
): IWorkflowLinkRef | null {
  if (!url || !currentHost) {
    return null
  }

  let parsedUrl: URL
  try {
    parsedUrl = new URL(url)
  } catch {
    return null
  }

  if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
    return null
  }

  if (canonicalizeHost(parsedUrl.host) !== canonicalizeHost(currentHost)) {
    return null
  }

  const pathMatch = workflowPathRegex.exec(parsedUrl.pathname)
  if (!pathMatch) {
    return null
  }

  const [, projectCode, workflowIdFromPath] = pathMatch
  const workflowId =
    workflowIdFromPath || parsedUrl.searchParams.get(WorkflowUrlParamKey)

  if (!workflowId) {
    return null
  }

  return { projectCode, workflowId }
}

export function extractWorkflowLinks(
  text: string,
  currentHost: string,
): IWorkflowLinkRef[] {
  if (!text) {
    return []
  }

  const links: IWorkflowLinkRef[] = []
  const seenWorkflowIds = new Set<string>()
  const urls = text.match(new RegExp(urlRegex.source, 'gu')) || []

  for (const url of urls) {
    if (links.length >= MAX_WORKFLOW_CARDS_PER_MESSAGE) {
      break
    }

    const link = parseWorkflowLink(url, currentHost)
    if (link && !seenWorkflowIds.has(link.workflowId)) {
      seenWorkflowIds.add(link.workflowId)
      links.push(link)
    }
  }

  return links
}
